import { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { BACKEND_URL, apiHeaders } from "./config";

export default function ScraperDashboard() {
  const [scraperStatus, setScraperStatus] = useState({});
  const [loading, setLoading] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");

  const scrapers = [
    { name: "České ploty", key: "ceskeploty" },
    { name: "Pletiva Dobry", key: "pletivadobry" },
    { name: "Ploty Boppe", key: "boppe" },
    { name: "Pilecky", key: "pilecky" },
    { name: "MM Ploty", key: "mmploty" },
    { name: "E-Pletivo", key: "epletivo" },
    { name: "Plot Eshop", key: "ploteshop" },
  ];

  const pollRef = useRef(null);

  const stopPoll = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      const res = await fetch(`${BACKEND_URL}/api/scraper-status`, { headers: apiHeaders() }).catch(() => null);
      if (!res) return;
      const data = await res.json().catch(() => null);
      if (!data) return;
      setScraperStatus(data);
      const anyRunning = Object.values(data).some(s => s.bezi || s.ve_fronte);
      if (!anyRunning) stopPoll();
    }, 3000);
  }, []);

  const fetchScraperStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/scraper-status`, { headers: apiHeaders() });
      const data = await res.json();
      setScraperStatus(data);
      return data;
    } catch (error) {
      console.error("❌ Chyba při načítání statusu scraperů:", error);
      return null;
    }
  };

  const startScraper = async (key) => {
    setLoading((prev) => ({ ...prev, [key]: true }));
    try {
      await fetch(`${BACKEND_URL}/api/scrape/${key}`, { method: "POST", headers: apiHeaders() });
    } catch (e) {
      console.error(e);
    }
    setLoading((prev) => ({ ...prev, [key]: false }));
    await fetchScraperStatus();
    startPoll();
  };

  const startAllScrapers = async () => {
    for (const { key } of scrapers) {
      await startScraper(key);
    }
  };

  const handleExportToSheets = () => {
    setIsExporting(true);
    setExportStatus("🚀 Spouštím export do Google Sheets...");
    fetch("https://sheets-production-2b0c.up.railway.app/run-script").catch(() => {});
    setTimeout(() => {
      setIsExporting(false);
      setExportStatus("✅ Export byl odeslán");
    }, 3000);
  };

  useEffect(() => {
    fetchScraperStatus().then(data => {
      if (data && Object.values(data).some(s => s.bezi || s.ve_fronte)) startPoll();
    });
    return () => stopPoll();
  }, [startPoll]);

  function fmtDatum(d) {
    if (!d) return "–";
    return new Date(d).toLocaleString("cs-CZ", { timeZone: "Europe/Prague" });
  }

  return (
    <div className="scraper-container">
      <h1>🛠 Správa Scraperů</h1>

      <button onClick={startAllScrapers} disabled={scrapers.some(({ key }) => loading[key])} className="scraper-button all">
        🚀 Spustit všechny e-shopy
      </button>

      <button onClick={handleExportToSheets} disabled={isExporting} className="scraper-button export">
        📤 Export do Google Sheets
      </button>

      {exportStatus && <p className="export-status">{exportStatus}</p>}

      <div className="scraper-grid">
        {scrapers.map(({ name, key }) => {
          const status = scraperStatus[key] || {};
          const bezi = status.bezi;
          const veFronte = status.ve_fronte;
          const progress = status.progress || 0;
          const progressMsg = status.progress_msg || '';
          return (
            <div key={key} className="scraper-card">
              <button
                onClick={() => startScraper(key)}
                disabled={bezi || veFronte || loading[key]}
                className={`scraper-button ${key} ${bezi ? "running" : ""}`}
              >
                {bezi ? "🕵️‍♂️ Běžím..." : veFronte ? "⏳ Ve frontě..." : `🛒 ${name}`}
              </button>
              {(bezi || veFronte) && (
                <div className="scraper-progress">
                  <div className="progress-bar-track">
                    <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="progress-label">{progress}% {progressMsg && `— ${progressMsg}`}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="scraper-status">
        <h3>🕒 Poslední aktualizace:</h3>
        <ul>
          {Object.entries(scraperStatus).map(([shop, info]) => (
            <li key={shop}>
              <strong>{shop}:</strong> {fmtDatum(info.posledni_spusteni)}
              {info.bezi && <em> (běží…)</em>}
            </li>
          ))}
        </ul>
      </div>

    </div>
  );
}
